import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from '../context/BookingContext';
import './PrintPage.css';

const PrintPage = () => {
  const navigate = useNavigate();
  const { selectedVehicle, searchCriteria, quoteData, bookingDetails } = useBooking();

  useEffect(() => {
    if (!selectedVehicle || !quoteData) {
      navigate('/');
    }
  }, [selectedVehicle, quoteData, navigate]);

  const handlePrint = () => {
    window.print();
  };

  if (!selectedVehicle || !quoteData) {
    return null;
  }

  const calculateTotal = () => {
    let total = parseFloat(quoteData.pricing.total);
    
    quoteData.insurances.forEach(insurance => {
      if (bookingDetails.selectedInsurances[insurance.id]) {
        total += insurance.price;
      }
    });
    
    return total;
  };

  return (
    <div className="print-page">
      <div className="print-container">
        <div className="print-header no-print">
          <button className="back-button" onClick={() => navigate(-1)}>
            ← Volver
          </button>
          <button className="print-button-action" onClick={handlePrint}>
            🖨️ Imprimir
          </button>
        </div>

        <div className="print-content">
          <div className="print-logo">
            <h1>Taraborelli Rent a Car</h1>
            <p>Cotización de Reserva</p>
          </div>

          <div className="print-section">
            <h2>Detalles de la Búsqueda</h2>
            <div className="detail-grid">
              <div className="detail-item">
                <strong>Lugar de entrega:</strong>
                <span>{searchCriteria.pickupLocation}</span>
              </div>
              <div className="detail-item">
                <strong>Fecha de entrega:</strong>
                <span>
                  {new Date(searchCriteria.pickupDate).toLocaleDateString('es-AR')} - {searchCriteria.pickupTime}
                </span>
              </div>
              <div className="detail-item">
                <strong>Lugar de devolución:</strong>
                <span>{searchCriteria.returnLocation}</span>
              </div>
              <div className="detail-item">
                <strong>Fecha de devolución:</strong>
                <span>
                  {new Date(searchCriteria.returnDate).toLocaleDateString('es-AR')} - {searchCriteria.returnTime}
                </span>
              </div>
              <div className="detail-item">
                <strong>Duración:</strong>
                <span>{quoteData.days} días</span>
              </div>
            </div>
          </div>

          <div className="print-section">
            <h2>Vehículo Seleccionado</h2>
            <div className="vehicle-info-print">
              <img src={selectedVehicle.image || '/placeholder-car.svg'} alt={selectedVehicle.name} />
              <div>
                <h3>{selectedVehicle.name}</h3>
                <p>{selectedVehicle.category}</p>
                <div className="vehicle-features-print">
                  {selectedVehicle.features && (
                    <>
                      <span>👥 {selectedVehicle.features.passengers} pasajeros</span>
                      <span>🚪 {selectedVehicle.features.doors} puertas</span>
                      <span>⚙️ {selectedVehicle.features.transmission}</span>
                      {selectedVehicle.features.airConditioning && <span>❄️ A/C</span>}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {Object.values(bookingDetails.selectedInsurances).some(v => v) && (
            <div className="print-section">
              <h2>Seguros Seleccionados</h2>
              <ul className="selected-list">
                {quoteData.insurances.map(insurance => (
                  bookingDetails.selectedInsurances[insurance.id] && (
                    <li key={insurance.id}>
                      <strong>{insurance.name}</strong>
                      <span>$ {insurance.price.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
                    </li>
                  )
                ))}
              </ul>
            </div>
          )}

          {Object.values(bookingDetails.additionals).some(v => v > 0) && (
            <div className="print-section">
              <h2>Adicionales Seleccionados</h2>
              <ul className="selected-list">
                {quoteData.additionals.map(additional => (
                  bookingDetails.additionals[additional.id] > 0 && (
                    <li key={additional.id}>
                      <strong>{additional.name} x {bookingDetails.additionals[additional.id]}</strong>
                      <span>
                        $ {(additional.pricePerDay * bookingDetails.additionals[additional.id] * quoteData.days).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                      </span>
                    </li>
                  )
                ))}
              </ul>
            </div>
          )}

          <div className="print-section">
            <h2>Desglose de Precios</h2>
            <table className="price-table">
              <tbody>
                {quoteData.pricing.items.map((item, index) => (
                  <tr key={index}>
                    <td>{item.label}</td>
                    <td className="price-value">
                      $ {parseFloat(item.value).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td><strong>TOTAL</strong></td>
                  <td className="price-value">
                    <strong>$ {calculateTotal().toLocaleString('es-AR', { minimumFractionDigits: 2 })}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {bookingDetails.formData.firstName && (
            <div className="print-section">
              <h2>Datos del Conductor</h2>
              <div className="detail-grid">
                <div className="detail-item">
                  <strong>Nombre:</strong>
                  <span>{bookingDetails.formData.firstName} {bookingDetails.formData.lastName}</span>
                </div>
                <div className="detail-item">
                  <strong>Documento:</strong>
                  <span>{bookingDetails.formData.documentType} {bookingDetails.formData.documentNumber}</span>
                </div>
                <div className="detail-item">
                  <strong>Email:</strong>
                  <span>{bookingDetails.formData.email}</span>
                </div>
                <div className="detail-item">
                  <strong>Teléfono:</strong>
                  <span>{bookingDetails.formData.phone}</span>
                </div>
              </div>
            </div>
          )}

          <div className="print-section">
            <h2>Garantías</h2>
            <ul className="guarantees-list">
              {quoteData.guarantees.map((guarantee, index) => (
                <li key={index}>
                  <span>{guarantee.text}:</span>
                  <strong>{guarantee.value}</strong>
                </li>
              ))}
            </ul>
          </div>

          <div className="print-footer">
            <p>Esta cotización es válida por 48 horas</p>
            <p>Fecha de emisión: {new Date().toLocaleDateString('es-AR')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintPage;
